
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { AspectRatio } from "@/components/ui/aspect-ratio";
import DynamicTitle from '@/components/DynamicTitle';
import SEOStructuredData from '@/components/SEOStructuredData';
import SEOMetaTags from '@/components/SEOMetaTags';

export default function About() {
  return (
    <div className="min-h-screen bg-black">
      <SEOMetaTags
        title="Who is Aram AI? - About the Armenian AI Solopreneur"
        description="Learn about Aram AI, an Armenian AI solopreneur and educator specializing in creative AI applications, community building, and AI education for Armenia's future."
        keywords="Aram AI, Armenian AI solopreneur, AI education, Armenian technology, AI consultant, artificial intelligence Armenia, tech entrepreneur"
        canonicalUrl="https://tezzarida.io/about"
        ogType="website"
        ogImage="https://tezzarida.io/lovable-uploads/b6e5b9f0-8ab9-4fc9-8da4-19631fb7a6f1.png"
      />
      <DynamicTitle 
        title="Who is Aram AI? - About the Armenian AI Solopreneur"
        description="Learn about Aram AI, an Armenian AI solopreneur and educator specializing in creative AI applications, community building, and AI education for Armenia's future."
        keywords="Aram AI, Armenian AI solopreneur, AI education, Armenian technology, AI consultant, artificial intelligence Armenia, tech entrepreneur"
        canonicalUrl="https://tezzarida.io/about"
      />
      <SEOStructuredData 
        type="person"
        title="Aram AI - Armenian AI Solopreneur & Educator"
        description="Aram AI is an Armenian AI Solopreneur and educator, specializing in creative AI applications and community building in Armenia."
        url="https://tezzarida.io/about"
        image="https://tezzarida.io/lovable-uploads/b6e5b9f0-8ab9-4fc9-8da4-19631fb7a6f1.png"
      />
      <Header />
      
      <main className="container mx-auto px-4 py-12 max-w-4xl">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 font-heading glow-text">
            Who is Aram AI?
          </h1>
        </div>

        {/* Hero Image */}
        <div className="mb-12">
          <AspectRatio ratio={16/9} className="overflow-hidden rounded-lg">
            <img 
              src="/lovable-uploads/b6e5b9f0-8ab9-4fc9-8da4-19631fb7a6f1.png" 
              alt="Aram AI portrait" 
              className="w-full h-full object-cover"
            />
          </AspectRatio>
        </div>

        {/* Article Content - Centered with glass panel styling */}
        <div className="max-w-4xl mx-auto mb-24">
          <article className="glass-panel p-10 rounded-2xl">
            <div className="space-y-8">
              <p className="text-xl leading-relaxed text-gray-200 font-light">
                Aram AI is an Armenian AI Solopreneur and educator, specializing in creative AI applications and community building. He is driven by a core mission: to foster AI education and cultivate a thriving AI ecosystem within Armenia. Though currently based in Spain after being forced to leave his homeland, his work is deeply dedicated to empowering Armenia's future through technology.
              </p>

              <div className="space-y-6">
                <p className="text-lg text-gray-300 font-medium">
                  He channels his expertise and passion through several key initiatives:
                </p>

                <div className="space-y-6 pl-4 border-l-2 border-purple-500/30">
                  <div className="space-y-4">
                    <div className="group">
                      <p className="text-lg text-gray-200 leading-relaxed">
                        <a href="/" className="text-purple-300 hover:text-purple-200 transition-all duration-300 font-semibold hover:underline decoration-2 underline-offset-4">tezzarida.io</a>: Aram AI's blog, sharing valuable insights, strategies, and experiences on the journey to becoming a top AI solopreneur and building impactful AI businesses.
                      </p>
                    </div>
                    
                    <div className="group">
                      <p className="text-lg text-gray-200 leading-relaxed">
                        <a href="https://t.me/haieren" target="_blank" rel="noreferrer" className="text-purple-300 hover:text-purple-200 transition-all duration-300 font-semibold hover:underline decoration-2 underline-offset-4">"հAIերեն"</a>: Creator of "հAIերեն," the first AI-automated newsletter in the Armenian language, making cutting-edge AI knowledge accessible to his community.
                      </p>
                    </div>
                    
                    <div className="group">
                      <p className="text-lg text-gray-200 leading-relaxed">
                        <a href="https://www.instagram.com/iamaram.ai/" target="_blank" rel="noreferrer" className="text-purple-300 hover:text-purple-200 transition-all duration-300 font-semibold hover:underline decoration-2 underline-offset-4">Armenian AI Content</a>: Actively produces educational content about AI on YouTube and Instagram in Armenian, breaking down complex topics for his native audience.
                      </p>
                    </div>
                    
                    <div className="group">
                      <p className="text-lg text-gray-200 leading-relaxed">
                        <span className="text-purple-300 font-semibold">AI Automation Services</span>: As a consultant, Aram helps businesses automate processes and integrate AI solutions, leveraging over 10,000 hours of freelance experience and a track record of successful project delivery.
                      </p>
                    </div>
                    
                    <div className="group">
                      <p className="text-lg text-gray-200 leading-relaxed">
                        <a href="https://promptmanager.pro" target="_blank" rel="noreferrer" className="text-purple-300 hover:text-purple-200 transition-all duration-300 font-semibold hover:underline decoration-2 underline-offset-4">Prompt Manager</a>: The developer behind Prompt Manager ("Your Personal Prompt Library"), an experimental SaaS platform that gained early traction, showcasing his ability to "vibe code" and rapidly prototype AI tools.
                      </p>
                    </div>
                    
                    <div className="group">
                      <p className="text-lg text-gray-200 leading-relaxed">
                        <a href="https://vasilenko.io" target="_blank" rel="noreferrer" className="text-purple-300 hover:text-purple-200 transition-all duration-300 font-semibold hover:underline decoration-2 underline-offset-4">Vasilenko AI</a>: Creator of "the funniest memecoin on TON," Vasilenko AI, a side project demonstrating his experimental approach to new technologies and community building.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <p className="text-xl leading-relaxed text-gray-200 font-light pt-4">
                Aram's journey from a student in Armenia to building an AI-focused career from scratch in a new country has fueled his determination to return and contribute his skills. His ultimate goal is to foster an 'AI-first' mindset within Armenia, enabling individuals and businesses to harness the power of current AI tools and supporting a community skilled in AI application.
              </p>
            </div>
          </article>
        </div>
      </main>

      <Footer />
    </div>
  );
}
